import type { OutreachSeedResponse } from 'shared';
import { customer, vehicle } from '@/state/session.js';
import { config } from '@/state/config.js';
import { formatInteger } from '@/lib/formatters.js';
import { parseMileage } from '@/lib/mileage.js';

/** Derived from the wire schema so a new outreach type fails to compile, not silently misroutes. */
export type SeedType = OutreachSeedResponse['type'];

export type StepResult = { kind: 'next' } | { kind: 'rate' } | { kind: 'dead-end'; copy: string };

/** Evaluated when the bubble lands, so it reads the session as it stands at that moment. */
type BubbleSpec = () => string;

export interface ContinueStep {
  kind: 'continue';
  bubbles: BubbleSpec[];
  label: string;
  customerEcho: () => string;
  onComplete: () => StepResult;
}
interface YesNoStep {
  kind: 'yes-no';
  bubbles: BubbleSpec[];
  prompt: string;
  customerEcho: (answer: 'yes' | 'no') => string;
  onYes: () => StepResult;
  onNo: () => StepResult;
}
interface InputStep {
  kind: 'input';
  bubbles: BubbleSpec[];
  inputLabel: string;
  inputMode?: 'numeric' | 'text';
  validate?: (value: string) => string | null;
  customerEcho: (value: string) => string;
  onSubmit: (value: string) => StepResult;
}
type ChatStep = ContinueStep | YesNoStep | InputStep;

function formatMileageEcho(value: string): string {
  const n = parseMileage(value);
  if (n === null) return `${value} miles`;
  return `${formatInteger(n)} miles`;
}

/**
 * One tap to pricing. Every scripted step is a chance to lose someone who opened this from
 * a text, so a step earns its place only by collecting something we can't rate without —
 * which here is nothing.
 */
const RECONSIDERATION_SCRIPT: ChatStep[] = [
  {
    kind: 'continue',
    bubbles: [
      () =>
        `Hi ${customer.value?.firstName ?? 'there'}, this is ${config.value?.accountName ?? ''}.`,
      () => "You skipped a few plans on your purchase — here's another look at them.",
    ],
    label: 'Show me',
    customerEcho: () => 'Show me',
    onComplete: () => ({ kind: 'rate' }),
  },
];

/**
 * Two taps to pricing. Both steps earn their place: the ownership gate is the only thing
 * that can dead-end a renewal, and mileage is priced on, so it can't be inferred. The
 * greeting rides along with the gate rather than costing a tap of its own.
 */
const RENEWAL_SCRIPT: ChatStep[] = [
  {
    kind: 'yes-no',
    bubbles: [
      () =>
        `Hi ${customer.value?.firstName ?? 'there'}, this is ${config.value?.accountName ?? ''}.`,
      () => {
        const v = vehicle.value;
        const descriptor = [v?.year, v?.make, v?.model].filter(Boolean).join(' ') || 'vehicle';
        return `Your protection on your ${descriptor} is up for renewal.`;
      },
    ],
    prompt: 'Do you still own this vehicle?',
    customerEcho: (answer) => (answer === 'yes' ? 'Yes, I still own it' : "No, I don't anymore"),
    onYes: () => ({ kind: 'next' }),
    onNo: () => ({
      kind: 'dead-end',
      copy: 'Got it — please reach out to your dealer for next steps.',
    }),
  },
  {
    kind: 'input',
    bubbles: [() => "What's your current mileage? I'll price your options from it."],
    inputLabel: 'Current mileage',
    inputMode: 'numeric',
    validate: (v) => (parseMileage(v) === null ? 'Enter your mileage in numbers.' : null),
    customerEcho: (value) => formatMileageEcho(value),
    onSubmit: (value) => {
      const n = parseMileage(value);
      const prev = vehicle.value;
      // `validate` already rejected null, so this is belt-and-braces — but never
      // write a garbage 0 onto the vehicle we're about to rate.
      if (prev !== null && n !== null) vehicle.value = { ...prev, mileage: n };
      return { kind: 'rate' };
    },
  },
];

/**
 * Dealer voice past the scripted steps, where the flow is driven by API results rather
 * than by a step definition. `lib/error-copy.ts` holds only copy derived from an error
 * value; nothing here takes one, so it all lives with the script it continues.
 */
export const dealerCopy = {
  rateAck: (retry: boolean) =>
    retry ? 'Trying again — fetching plans for you now…' : 'Great, fetching plans for you now…',
  tiersIntro: (flowType: SeedType) =>
    flowType === 'renewal'
      ? 'Here are your renewal options.'
      : "Here's what you didn't pick last time:",
  /** Payment is settled one plan at a time, so the prompt always names the plan. */
  paymentPrompt: (productName: string) => `How would you like to pay for ${productName}?`,
  /** Confirms the action, not the terms — the header cart is where the order is described,
   *  and restating it here would give the customer two places to disagree. */
  planConfirmed: (productName: string) => `${productName} — added to your order.`,
  addAnotherPrompt: 'Would you like to add another plan?',
  noMoreProducts: "That's everything we have for your vehicle.",
  backToTiers: 'Sure — pick another plan below.',
  checkoutIntro: "Great choice! Let's confirm your details before checkout.",
  summaryIntro: "Here's everything before you check out.",
  connecting: 'Securely connecting you to checkout…',

  /** Dead end: without a vehicle there is nothing left to rate. */
  lostVehicle:
    "We've lost track of your vehicle details. Reach out to your dealer and they can pick this up.",
  /**
   * An empty rate result with unavailable providers is an outage, not an absence of
   * coverage — don't tell the customer their vehicle can't be covered when the truth
   * is that we couldn't reach anyone.
   */
  noPlans: (hadUnavailableProviders: boolean) =>
    hadUnavailableProviders
      ? "I couldn't reach our coverage providers just now. Try this link again in a few minutes, or reach out to us directly."
      : "We couldn't find any plans for your vehicle right now.",
  missingSessionDetails: 'Your session is missing some details. Reload the link and try again.',
};

export function getScriptFor(flowType: SeedType): ChatStep[] {
  return flowType === 'reconsideration' ? RECONSIDERATION_SCRIPT : RENEWAL_SCRIPT;
}
